/**
 * The intake for a call, read once — for screens that did not stream the call
 * themselves, like the service-request form opened from the CallHUD.
 *
 * Read-only on purpose: the form arrives after the extraction happened, so it
 * consumes the result rather than feeding it. Fails soft — a call with no
 * intake, a tenant with the feature off, and an extractor that is down all
 * produce `null`, and the form simply opens the way it always did.
 */

import { useQuery } from '@tanstack/react-query';
import { callIntakeApi, type CallIntake } from '@/lib/calls/intakeApi';

export const callIntakeKeys = {
  detail: (callControlId: string) => ['call-intake', callControlId] as const,
};

/**
 * `live` is for the form opened WHILE the call is still up — the common case,
 * since the agent starts booking the moment they know what the job is. The
 * customer is still talking, so the intake is still growing, and a form holding
 * a snapshot from thirty seconds ago is a form missing the access code they
 * just gave. Off by default: a form opened after the call has nothing to wait
 * for, and polling it forever would be a request every few seconds for a row
 * that will never change again.
 */
export function useCallIntakeQuery(
  callControlId: string | null | undefined,
  options?: { live?: boolean },
) {
  const live = options?.live ?? false;
  return useQuery<CallIntake | null>({
    queryKey: callIntakeKeys.detail(callControlId ?? 'none'),
    enabled: Boolean(callControlId),
    // One read as the form mounts, unless the call it came from is still going.
    staleTime: live ? 0 : 30_000,
    refetchInterval: live ? 8_000 : false,
    // Only while the tab is in front: an agent who switched away is not reading
    // this, and the intake is re-read the moment they come back.
    refetchIntervalInBackground: false,
    retry: false,
    queryFn: async () => {
      if (!callControlId) return null;
      try {
        return await callIntakeApi.get(callControlId);
      } catch {
        return null;
      }
    },
  });
}
